import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePaperLogScores } from "@/hooks/usePaperLogScores";
import {
  CheckCircle2, XCircle, RotateCcw, Wand2, ClipboardCheck, Gauge, Info,
  Sun, CloudSnow, MoonStar, Coffee, MapPin, Save, Trophy,
} from "lucide-react";

// Four Hours-of-Service duty statuses (rows of a driver's daily log grid).
const STATUSES = [
  { key: "OFF", label: "Off Duty", short: "1 · Off Duty", fill: "bg-slate-400" },
  { key: "SB", label: "Sleeper Berth", short: "2 · Sleeper", fill: "bg-indigo-400" },
  { key: "D", label: "Driving", short: "3 · Driving", fill: "bg-brand" },
  { key: "ON", label: "On Duty (not driving)", short: "4 · On Duty", fill: "bg-amber-400" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

// --- grid helpers ------------------------------------------------------------
const build = (spec) => {
  const g = Array(24).fill("OFF");
  spec.forEach(([status, from, to]) => { for (let h = from; h <= to; h++) g[h] = status; });
  return g;
};
const drivingHours = (g) => g.filter((s) => s === "D").length;
const longestBlock = (g, set) => {
  let max = 0, cur = 0;
  for (const s of g) { if (set.includes(s)) { cur++; max = Math.max(max, cur); } else cur = 0; }
  return max;
};
const breakOk = (g, limit = 8) => { // false if driving > limit consecutive hours without a break
  let c = 0;
  for (const s of g) { if (s === "D") { c++; if (c > limit) return false; } else c = 0; }
  return true;
};
const windowViolation = (g, hours) => {
  const start = g.findIndex((s) => s === "D" || s === "ON");
  if (start < 0) return false;
  for (let h = start + hours; h < 24; h++) if (g[h] === "D") return true;
  return false;
};
const blocks = (g) => {
  const out = []; let i = 0;
  while (i < 24) { const s = g[i]; let j = i; while (j < 24 && g[j] === s) j++; out.push({ status: s, len: j - i }); i = j; }
  return out;
};
const dutySpan = (g) => { // hours from first to last on-duty/driving hour (inclusive)
  const idx = g.map((s, i) => ({ s, i })).filter((x) => x.s === "D" || x.s === "ON");
  if (!idx.length) return 0;
  return idx[idx.length - 1].i - idx[0].i + 1;
};

// --- scenarios ---------------------------------------------------------------
const rule = (name, pass, detail) => ({ rule: name, pass, detail });

const SCENARIOS = [
  {
    key: "standard",
    label: "Standard day",
    icon: Sun,
    goal: "Log a 10-hour reset, then up to 11 hours driving with a 30-minute break before 8 straight hours — all inside a 14-hour window.",
    example: build([["OFF", 0, 9], ["ON", 10, 10], ["D", 11, 14], ["ON", 15, 15], ["D", 16, 22]]),
    evaluate: (g) => {
      const d = drivingHours(g), rest = longestBlock(g, ["OFF", "SB"]), win = windowViolation(g, 14), brk = breakOk(g, 8);
      return [
        rule("11-hour driving limit", d <= 11, `You logged ${d} h driving. The limit is 11 hours after a 10-hour break.`),
        rule("14-hour driving window", !win, win ? "You drove after the 14th hour of your workday. No driving once 14 hours pass from going on duty." : "All your driving stayed inside the 14-hour window."),
        rule("30-minute break", brk, brk ? "You broke up your driving before 8 straight hours. Good." : "You drove more than 8 hours straight — a 30-minute break is required first."),
        rule("10-hour reset", rest >= 10, rest >= 10 ? `You logged a ${rest}-hour reset — your legal restart.` : `Your longest off-duty block was ${rest} h. You need 10 consecutive hours off (Off Duty or Sleeper).`),
      ];
    },
  },
  {
    key: "adverse",
    label: "Adverse conditions",
    icon: CloudSnow,
    goal: "Snow, fog or unexpected traffic hit. The adverse-driving exception lets you extend driving to 13 hours and the window to 16 hours. You still need your break and 10-hour reset.",
    example: build([["OFF", 0, 9], ["D", 10, 17], ["OFF", 18, 18], ["D", 19, 23]]),
    evaluate: (g) => {
      const d = drivingHours(g), rest = longestBlock(g, ["OFF", "SB"]), win = windowViolation(g, 16), brk = breakOk(g, 8);
      return [
        rule("Driving limit (adverse +2 = 13h)", d <= 13, `You logged ${d} h driving. Adverse conditions let you push the 11-hour limit to 13.`),
        rule("Window (adverse +2 = 16h)", !win, win ? "You drove past the 16-hour extended window. Even with adverse conditions, that's a violation." : "Your driving stayed inside the extended 16-hour window."),
        rule("30-minute break", brk, brk ? "You still took your break before 8 straight hours of driving." : "You drove more than 8 hours straight — the 30-minute break still applies in adverse conditions."),
        rule("10-hour reset", rest >= 10, rest >= 10 ? `You logged a ${rest}-hour reset.` : `Your longest off-duty block was ${rest} h — you still need a 10-hour reset.`),
      ];
    },
  },
  {
    key: "split",
    label: "Split sleeper",
    icon: MoonStar,
    goal: "Split your 10 hours into two: one period of 7+ hours in the sleeper berth, plus another 2+ hours off duty or sleeper (10 hours total). The 7-hour sleeper period pauses your 14-hour clock.",
    example: build([["SB", 0, 6], ["ON", 7, 7], ["D", 8, 12], ["SB", 13, 15], ["ON", 16, 16], ["D", 17, 22]]),
    evaluate: (g) => {
      const d = drivingHours(g), brk = breakOk(g, 8);
      const bs = blocks(g);
      const big = bs.filter((b) => b.status === "SB" && b.len >= 7);
      const small = bs.filter((b) => (b.status === "SB" || b.status === "OFF") && b.len >= 2);
      let splitOk = false, pair = null;
      for (const a of big) for (const b of small) if (a !== b && a.len + b.len >= 10) { splitOk = true; pair = [a.len, b.len]; }
      return [
        rule("7+ hour sleeper period", big.length > 0, big.length > 0 ? `Found a ${Math.max(...big.map((b) => b.len))}-hour sleeper period — that's your qualifying long break.` : "No 7-hour sleeper-berth block found. One period of the split must be 7+ hours in the sleeper."),
        rule("Valid 10-hour split", splitOk, splitOk ? `Your ${pair[0]}h + ${pair[1]}h periods form a legal split (10+ hours total).` : "Pair a 7+ hour sleeper period with another 2+ hour off/sleeper period totaling at least 10 hours."),
        rule("11-hour driving limit", d <= 11, `You logged ${d} h driving. The 11-hour limit still applies across the split.`),
        rule("30-minute break", brk, brk ? "You broke up your driving before 8 straight hours." : "You drove more than 8 hours straight — take your 30-minute break."),
      ];
    },
  },
  {
    key: "restart",
    label: "34-hour restart",
    icon: Coffee,
    goal: "You're resetting your weekly 60/70-hour clock. A 34-hour restart is 34 straight hours off duty — so a restart day is a FULL day (all 24 hours) Off Duty or Sleeper. Any driving or on-duty time breaks the restart.",
    example: build([["OFF", 0, 5], ["SB", 6, 13], ["OFF", 14, 23]]),
    evaluate: (g) => {
      const d = drivingHours(g), on = g.filter((s) => s === "ON").length, offSb = g.filter((s) => s === "OFF" || s === "SB").length;
      return [
        rule("Zero driving hours", d === 0, d === 0 ? "No driving — good, driving would break the restart." : `You logged ${d} h driving. A restart period must be completely off duty.`),
        rule("Zero on-duty time", on === 0, on === 0 ? "No on-duty work logged — the clock keeps resetting." : `You logged ${on} h on duty. Even non-driving work stops a restart.`),
        rule("Full 24 hours off duty / sleeper", offSb === 24, offSb === 24 ? "The whole day is off duty or sleeper — part of a valid 34-hour restart." : `Only ${offSb} of 24 hours are off duty. A restart day must be entirely off.`),
      ];
    },
  },
  {
    key: "shorthaul",
    label: "Short-haul exception",
    icon: MapPin,
    goal: "Short-haul drivers (within a 150 air-mile radius, back to the same location) are exempt from the 30-minute break and full logs — but you still can't drive past 11 hours, must be done within a 14-hour day, and need a 10-hour reset first.",
    example: build([["OFF", 0, 9], ["ON", 10, 10], ["D", 11, 21], ["OFF", 22, 23]]),
    evaluate: (g) => {
      const d = drivingHours(g), rest = longestBlock(g, ["OFF", "SB"]), span = dutySpan(g);
      return [
        rule("11-hour driving limit", d <= 11, `You logged ${d} h driving. The 11-hour limit still applies to short-haul drivers.`),
        rule("Back within a 14-hour day", span > 0 && span <= 14, span <= 14 ? `Your workday spanned ${span} h — you were done within 14 hours.` : `Your workday spanned ${span} h. Short-haul drivers must return within 14 hours.`),
        rule("10-hour reset", rest >= 10, rest >= 10 ? `You logged a ${rest}-hour reset.` : `Your longest off-duty block was ${rest} h — you still need a 10-hour reset.`),
        rule("30-minute break — exempt", true, "Short-haul drivers within 150 air-miles are exempt from the 30-minute break rule, so this one's on the house."),
      ];
    },
  },
];

const evaluate = (grid, scenario) => {
  const results = scenario.evaluate(grid);
  const passed = results.filter((r) => r.pass).length;
  return {
    results, passed, total: results.length,
    allPass: passed === results.length,
    score: Math.round((passed / results.length) * 100),
    driving: grid.filter((x) => x === "D").length,
  };
};

const QUESTIONS = [
  {
    q: "Your ELD stops working while you're on the road. What are you supposed to do?",
    options: ["Keep driving and ignore it until you get home", "Switch to paper logs, note the malfunction, and reconstruct your current day plus the past 7 days", "Park the truck until a mechanic replaces the ELD", "Call the broker and cancel the load"],
    answer: 1,
    explain: "When an ELD malfunctions you must note it, keep running on paper logs, and reconstruct your Record of Duty Status for the current 24 hours and the prior 7 days.",
  },
  {
    q: "After a 10-hour break, how many hours may a property-carrying driver drive?",
    options: ["8 hours", "10 hours", "11 hours", "14 hours"], answer: 2,
    explain: "The driving limit is 11 hours after 10 consecutive hours off duty.",
  },
  {
    q: "You may NOT drive after the ___th hour once you come on duty.",
    options: ["11th", "12th", "14th", "16th"], answer: 2,
    explain: "The 14-hour rule: driving isn't allowed after the 14th consecutive hour following your start of duty, even with breaks.",
  },
  {
    q: "When is a 30-minute break required?",
    options: ["After 4 hours of driving", "After 8 cumulative hours of driving", "Only if you feel tired", "Once every 24 hours"], answer: 1,
    explain: "A 30-minute break is required before you drive past 8 cumulative hours of driving time.",
  },
  {
    q: "How long can you legally keep running on paper logs while your ELD is repaired or replaced?",
    options: ["24 hours", "3 days", "Up to 8 days", "Up to 30 days"], answer: 2,
    explain: "You may use paper logs for up to 8 days while the ELD is being repaired or replaced.",
  },
  {
    q: "Under the adverse driving conditions exception, driving and the window can each be extended by up to how many hours?",
    options: ["1 hour", "2 hours", "3 hours", "You can't extend them"], answer: 1,
    explain: "Unexpected adverse conditions let you extend the 11-hour driving limit and 14-hour window by up to 2 hours (to 13 and 16).",
  },
  {
    q: "In a split sleeper-berth, one of the two periods must be at least how many hours in the sleeper?",
    options: ["2 hours", "5 hours", "7 hours", "10 hours"], answer: 2,
    explain: "A valid split pairs a period of at least 7 hours in the sleeper berth with another period of at least 2 hours (off duty or sleeper), totaling 10.",
  },
  {
    q: "A 34-hour restart resets which clock?",
    options: ["The 11-hour driving limit", "The 14-hour window", "Your 60/70-hour weekly on-duty clock", "Nothing — it's optional rest"], answer: 2,
    explain: "34 consecutive hours off duty restarts your 60-hour/7-day or 70-hour/8-day on-duty total.",
  },
  {
    q: "The short-haul exception applies within how many air-miles of your work reporting location?",
    options: ["50 air-miles", "100 air-miles", "150 air-miles", "300 air-miles"], answer: 2,
    explain: "Short-haul drivers operating within a 150 air-mile radius and returning to the same location are exempt from full logs and the 30-minute break.",
  },
];

export default function PaperLogTrainer() {
  const [scenarioKey, setScenarioKey] = useState("standard");
  const scenario = SCENARIOS.find((s) => s.key === scenarioKey);
  const [grid, setGrid] = useState(() => Array(24).fill("OFF"));
  const [report, setReport] = useState(null);
  const painting = useRef(false);
  const paintStatus = useRef(null);
  const [answers, setAnswers] = useState({});
  const { scores, saveAttempt } = usePaperLogScores();

  useEffect(() => {
    const up = () => { painting.current = false; };
    window.addEventListener("pointerup", up);
    return () => window.removeEventListener("pointerup", up);
  }, []);

  const setHour = (h, status) => setGrid((g) => { const n = [...g]; n[h] = status; return n; });
  const onDown = (h, status) => { painting.current = true; paintStatus.current = status; setHour(h, status); setReport(null); };
  const onEnter = (h) => { if (painting.current) setHour(h, paintStatus.current); };

  const reset = () => { setGrid(Array(24).fill("OFF")); setReport(null); };
  const loadExample = () => { setGrid([...scenario.example]); setReport(null); };
  const check = () => setReport(evaluate(grid, scenario));
  const saveCurrent = () => {
    if (!report) return;
    const isBest = saveAttempt(scenarioKey, report.score, report.allPass);
    toast.success(isBest ? `New best for ${scenario.label}: ${report.score}%!` : `Saved — your best stays ${scores[scenarioKey]?.best ?? report.score}%.`);
  };
  const pickScenario = (k) => { setScenarioKey(k); setGrid(Array(24).fill("OFF")); setReport(null); };

  const score = QUESTIONS.reduce((acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0), 0);
  const answered = Object.keys(answers).length;

  return (
    <div className="space-y-10" data-testid="paperlog-trainer">
      {/* Scenario picker */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="paperlog-scenarios">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">Choose a challenge</div>
        <div className="grid sm:grid-cols-3 gap-3">
          {SCENARIOS.map((s) => {
            const Icon = s.icon;
            const active = s.key === scenarioKey;
            return (
              <button
                key={s.key}
                onClick={() => pickScenario(s.key)}
                data-testid={`scenario-${s.key}`}
                className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-brand bg-orange-50/50 ring-1 ring-brand/20" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${active ? "text-brand" : "text-slate-500"}`} />
                  <span className={`text-sm font-bold ${active ? "text-slate-900" : "text-slate-700"}`}>{s.label}</span>
                </div>
                {scores[s.key] && (
                  <div data-testid={`scenario-best-${s.key}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-500">
                    {scores[s.key].compliant && <Trophy className="w-3 h-3 text-amber-500" />}
                    Best {scores[s.key].best}%
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Interactive grid */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardCheck className="w-4 h-4 text-brand" />
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Practice grid · {scenario.label}</span>
        </div>
        <h3 className="font-heading text-xl font-black tracking-tight text-slate-900">Plot a legal 24-hour day</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-testid="scenario-goal">{scenario.goal}</p>

        <div className="mt-6 overflow-x-auto">
          <div data-testid="paperlog-grid" style={{ minWidth: 660, touchAction: "none" }}>
            {/* Hour ruler */}
            <div className="flex">
              <div className="w-32 shrink-0" />
              <div className="flex-1 grid" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                {HOURS.map((h) => (
                  <div key={h} className="text-[9px] text-slate-400 text-center tabular border-l border-slate-100">
                    {h % 3 === 0 ? (h === 0 ? "MID" : h === 12 ? "NOON" : h) : ""}
                  </div>
                ))}
              </div>
            </div>

            {/* Rows */}
            {STATUSES.map((s) => (
              <div key={s.key} className="flex items-stretch">
                <div className="w-32 shrink-0 flex items-center pr-3 text-xs font-bold text-slate-600 h-10">{s.short}</div>
                <div className="flex-1 grid border-t border-slate-200" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
                  {HOURS.map((h) => {
                    const active = grid[h] === s.key;
                    return (
                      <button
                        key={h}
                        data-testid={`log-cell-${s.key}-${h}`}
                        onPointerDown={() => onDown(h, s.key)}
                        onPointerEnter={() => onEnter(h)}
                        onClick={() => { setHour(h, s.key); setReport(null); }}
                        className={`h-10 border-l border-b border-slate-200 transition-colors ${active ? s.fill : "bg-white hover:bg-slate-50"}`}
                        aria-label={`${s.label} hour ${h}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live totals */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="paperlog-totals">
          {[
            { label: "Driving", v: grid.filter((x) => x === "D").length },
            { label: "On Duty", v: grid.filter((x) => x === "ON").length },
            { label: "Sleeper", v: grid.filter((x) => x === "SB").length },
            { label: "Off Duty", v: grid.filter((x) => x === "OFF").length },
          ].map((t) => (
            <div key={t.label} className="rounded-xl bg-slate-50 border border-slate-100 py-2">
              <div className="font-mono font-black text-lg text-slate-900 tabular">{t.v}h</div>
              <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">{t.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Button onClick={check} data-testid="paperlog-check" className="rounded-full bg-brand hover:bg-brand-hover text-white font-bold h-11 px-6">
            <Gauge className="w-4 h-4 mr-2" /> Check my log
          </Button>
          <Button onClick={loadExample} data-testid="paperlog-example" variant="outline" className="rounded-full border-slate-300 font-bold h-11 px-5">
            <Wand2 className="w-4 h-4 mr-2" /> Show a correct example
          </Button>
          <Button onClick={reset} data-testid="paperlog-reset" variant="ghost" className="rounded-full font-bold h-11 px-5 text-slate-500">
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
        </div>

        {report && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-3" data-testid="paperlog-report">
            <div className={`rounded-xl px-4 py-3 font-bold text-sm ${report.allPass ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="paperlog-verdict">
              {report.allPass ? "Clean log — this day is compliant. This is exactly what an inspector wants to see." : "Not compliant yet — fix the flagged items below and check again."}
            </div>
            <div className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="text-sm">
                <span className="font-black text-slate-900 tabular" data-testid="paperlog-score">Score: {report.score}%</span>
                <span className="text-slate-500"> · {report.passed}/{report.total} rules</span>
                {scores[scenarioKey] && <span className="text-slate-400"> · best {scores[scenarioKey].best}%</span>}
              </div>
              <Button onClick={saveCurrent} data-testid="paperlog-save" variant="outline" className="rounded-full border-slate-300 font-bold h-9 px-4 text-sm shrink-0">
                <Save className="w-4 h-4 mr-1.5" /> Save attempt
              </Button>
            </div>
            {report.results.map((r) => (
              <div key={r.rule} className="flex items-start gap-3 rounded-xl border border-slate-200 p-3" data-testid={`paperlog-rule-${r.pass ? "pass" : "fail"}`}>
                {r.pass ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
                <div>
                  <div className="text-sm font-bold text-slate-900">{r.rule}</div>
                  <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">{r.detail}</div>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        <p className="mt-5 flex items-start gap-2 text-[11px] text-slate-400 leading-relaxed">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          Simplified practice tool for property-carrying drivers — it checks the core HOS rules by the hour. Always follow current FMCSA rules and your carrier's policy.
        </p>
      </div>

      {/* Quiz */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7" data-testid="paperlog-quiz">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Knowledge check</span>
          {answered > 0 && <span className="text-xs font-bold text-slate-500 tabular" data-testid="paperlog-quiz-score">{score}/{QUESTIONS.length} correct</span>}
        </div>
        <h3 className="font-heading text-xl font-black tracking-tight text-slate-900">Can you spot the right answer?</h3>
        <p className="text-sm text-slate-600 mt-2">Quick check on the rules that keep you out of service.</p>

        <div className="mt-6 space-y-6">
          {QUESTIONS.map((q, qi) => {
            const chosen = answers[qi];
            const done = chosen !== undefined;
            return (
              <div key={qi} data-testid={`quiz-q-${qi}`}>
                <div className="text-sm font-bold text-slate-900 mb-3">{qi + 1}. {q.q}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const isChosen = chosen === oi;
                    const isCorrect = oi === q.answer;
                    let cls = "border-slate-200 hover:border-slate-300 text-slate-700";
                    if (done && isCorrect) cls = "border-emerald-300 bg-emerald-50 text-emerald-800";
                    else if (done && isChosen && !isCorrect) cls = "border-red-300 bg-red-50 text-red-700";
                    return (
                      <button
                        key={oi}
                        disabled={done}
                        onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                        data-testid={`quiz-q-${qi}-opt-${oi}`}
                        className={`w-full text-left rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors flex items-center justify-between gap-3 ${cls}`}
                      >
                        <span>{opt}</span>
                        {done && isCorrect && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                        {done && isChosen && !isCorrect && <XCircle className="w-4 h-4 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {done && <p className="mt-2 text-xs text-slate-500 leading-relaxed" data-testid={`quiz-q-${qi}-explain`}>{q.explain}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
